import { DinamikBilesen } from '@service/fikoyt-ui/decorators/dinamik-bilesen';
import { ChangeDetectionStrategy, Component, computed, input, model } from '@angular/core';
import { CommonModule } from '@angular/common';
import { GameCardBase, GC_BASE_CSS } from '@/src/app/uygulamalar/hmi-pano/pano/game-card-base';
import { paramDeger, kutukGeo } from '@/src/app/shared/utils/param-deger.utils';
import { FikoYTKokBilesenComponent } from '@/src/service/kok-bilesen/kok-bilesen.component';
import { CamKutukEditComponent } from './cam-kutuk-edit.component';
import { CamKutukKartSadeComponent } from './cam-kutuk-kart-sade.component';

const DURUM_RENK: Record<string, string> = { 'Bekliyor': '#94a3b8', 'Görüldü': '#3b82f6', 'Hazırlanıyor': '#f59e0b', 'Tamamlandı': '#22c55e', 'İptal': '#ef4444' };
const DURUM_RGB: Record<string, string> = { 'Bekliyor': '148,163,184', 'Görüldü': '59,130,246', 'Hazırlanıyor': '245,158,11', 'Tamamlandı': '34,197,94', 'İptal': '239,68,68' };
const DURUM_IKON: Record<string, string> = { 'Bekliyor': '⏳', 'Görüldü': '👁️', 'Hazırlanıyor': '🔨', 'Tamamlandı': '✅', 'İptal': '❌' };

@Component({
  selector: 'cam-kutuk-kart',
  standalone: true,
  imports: [CommonModule],
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
    <div class="gc gc--cam-kutuk" [style.--accent]="renk()" [style.--accent-rgb]="rgb()" (mousemove)="mouseMove($event)" (mouseleave)="mouseLeave()" [ngStyle]="kartStil">
      <div class="gc-canli-bg"></div><div class="gc-parlak-kenar"></div>
      <div class="gc-kose gc-kose--sol-ust"></div><div class="gc-kose gc-kose--sag-ust"></div><div class="gc-kose gc-kose--sol-alt"></div><div class="gc-kose gc-kose--sag-alt"></div>
      <div class="gc-kose-ic gc-kose-ic--sol-ust"></div><div class="gc-kose-ic gc-kose-ic--sag-ust"></div><div class="gc-kose-ic gc-kose-ic--sol-alt"></div><div class="gc-kose-ic gc-kose-ic--sag-alt"></div>
      <div class="gc-rarity-serit"></div><div class="gc-ic-glow"></div><div class="gc-desen"></div><div class="gc-shimmer"></div><div class="gc-holo"></div>
      <div class="gc-kenar-glow" [style.background]="kenarGlow"></div>
      <div class="gc-icerik">
        <div class="gc-hud-baslik">🪵 KÜTÜK</div>
        <div class="ck-svg-wrap">
          <svg [attr.viewBox]="g().vb" preserveAspectRatio="xMidYMid meet" class="ck-svg">
            <defs>
              <linearGradient id="gcUst" x1="0" y1="0" x2="1" y2="0"><stop offset="0%" stop-color="#fbbf24"/><stop offset="100%" stop-color="#f59e0b"/></linearGradient>
              <linearGradient id="gcOn" x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stop-color="#d97706"/><stop offset="100%" stop-color="#b45309"/></linearGradient>
              <linearGradient id="gcSag" x1="0" y1="0" x2="1" y2="1"><stop offset="0%" stop-color="#b45309"/><stop offset="100%" stop-color="#92400e"/></linearGradient>
              <marker id="gcB" viewBox="0 0 8 8" refX="4" refY="4" markerWidth="4" markerHeight="4" orient="auto-start-reverse"><path d="M1,1 L7,4 L1,7Z" fill="#60a5fa"/></marker>
              <marker id="gcG" viewBox="0 0 8 8" refX="4" refY="4" markerWidth="4" markerHeight="4" orient="auto-start-reverse"><path d="M1,1 L7,4 L1,7Z" fill="#4ade80"/></marker>
              <marker id="gcR" viewBox="0 0 8 8" refX="4" refY="4" markerWidth="4" markerHeight="4" orient="auto-start-reverse"><path d="M1,1 L7,4 L1,7Z" fill="#f87171"/></marker>
            </defs>
            <polygon [attr.points]="g().on" fill="url(#gcOn)"/>
            <polygon [attr.points]="g().sag" fill="url(#gcSag)"/>
            <polygon [attr.points]="g().ust" fill="url(#gcUst)"/>
            <polygon [attr.points]="g().on" fill="none" stroke="rgba(255,255,255,.2)" stroke-width="1" stroke-linejoin="round"/>
            <polygon [attr.points]="g().sag" fill="none" stroke="rgba(255,255,255,.1)" stroke-width="1" stroke-linejoin="round"/>
            <polygon [attr.points]="g().ust" fill="none" stroke="rgba(255,255,255,.3)" stroke-width="1" stroke-linejoin="round"/>
            <g [attr.transform]="'translate(0,' + (g().by + 16) + ')'">
              <line [attr.x1]="g().bx" y1="4" [attr.x2]="g().bx + g().w" y2="4" stroke="#60a5fa" stroke-width="1" marker-start="url(#gcB)" marker-end="url(#gcB)" opacity=".7"/>
              <text [attr.x]="g().bx + g().w/2" y="16" text-anchor="middle" fill="#93c5fd" font-size="9" font-weight="700" font-family="monospace">{{ g().enD }}</text>
            </g>
            <g [attr.transform]="'translate(' + (g().bx - 16) + ',0)'">
              <line x1="-4" [attr.y1]="g().by" x2="-4" [attr.y2]="g().by - g().h" stroke="#f87171" stroke-width="1" marker-start="url(#gcR)" marker-end="url(#gcR)" opacity=".7"/>
              <text x="-8" [attr.y]="g().by - g().h/2 + 3" text-anchor="end" fill="#fca5a5" font-size="9" font-weight="700" font-family="monospace">{{ g().yukD }}</text>
            </g>
            <line [attr.x1]="g().bx + g().w + 16" [attr.y1]="g().by" [attr.x2]="g().bx + g().w + g().dh + 16" [attr.y2]="g().by - g().dh" stroke="#4ade80" stroke-width="1" marker-start="url(#gcG)" marker-end="url(#gcG)" opacity=".7"/>
            <text [attr.x]="g().bx + g().w + g().dh*.5 + 24" [attr.y]="g().by - g().dh*.5 + 3" text-anchor="start" fill="#86efac" font-size="9" font-weight="700" font-family="monospace">{{ g().boyD }}</text>
          </svg>
        </div>
        <div class="ck-durum-badge" [style.background]="'rgba('+rgb()+',.15)'" [style.color]="renk()" [style.border-color]="'rgba('+rgb()+',.3)'">{{ sonDurum() }}</div>
        <div class="gc-xp-bar"><div class="gc-xp-dolgu" [style.width.%]="gorunur() ? ilerleme() : 0"></div></div>
      </div>
    </div>
  `,
  styles: [GC_BASE_CSS, `
    .ck-svg-wrap { width:100%; margin:2px 0; }
    .ck-svg { display:block; width:100%; height:80px; }
    .ck-durum-badge { display:inline-block; padding:2px 10px; border-radius:3px; font-size:10px; font-weight:700; letter-spacing:.06em; text-transform:uppercase; border:1px solid; margin-top:2px; }
  `],
})
export class CamKutukKartComponent extends GameCardBase {
  parametreler = input<any>();
  _hesap = input<any>();
  constructor() { super(); }
  d(ismi: string) { return paramDeger(this.parametreler()?.ek, ismi); }
  g = computed(() => kutukGeo(+(this.d('en') || 0), +(this.d('boy') || 0), +(this.d('yukseklik') || 0)));
  sonDurum = computed(() => this._hesap()?.sonDurum || this.d('durum') || 'Bekliyor');
  renk = computed(() => DURUM_RENK[this.sonDurum()] || '#94a3b8');
  rgb = computed(() => DURUM_RGB[this.sonDurum()] || '148,163,184');
  ilerleme = computed(() => this.sonDurum() === 'Tamamlandı' ? 100 : this.sonDurum() === 'Hazırlanıyor' ? 60 : this.sonDurum() === 'Görüldü' ? 30 : 10);
}

@DinamikBilesen({
  id: 'cam-kutuk',
  baslik: 'Kütük',
  aciklama: 'Kütük takibi',
  ikon: 'build',
  kategori: 'cam-isleme',
})
class CamKutuk {
  paylas_kutusu = CamKutukEditComponent;
  paylasilmis_kart = CamKutukKartComponent;
  paylasilmis_kart_sade = CamKutukKartSadeComponent;

  hesaplamalar = {
    prepipe: [
      { $lookup: { from: 'hareketler', let: { pid: '$_id' }, pipeline: [
        { $match: { $expr: { $eq: ['$_uid', '$$pid'] }, silindi: { $ne: true } } },
        { $lookup: { from: 'hareketler', localField: 'veri.icerikler', foreignField: '_id', as: 'ic' } },
        { $unwind: { path: '$ic', preserveNullAndEmptyArrays: false } },
        { $match: { 'ic.ek._secilenSetId': 'cam.kutuk.durum' } },
        { $sort: { _id: -1 } },
        { $limit: 1 }
      ], as: '_alt' } },
      { $addFields: {
        '_hesap.sonDurum': { $ifNull: [{ $arrayElemAt: ['$_alt.ic.ek.durum', 0] }, null] },
      }},
      { $project: { _alt: 0 } }
    ],
  };
}


@Component({
  selector: 'cam-kutuk-durum-kart-sade',
  standalone: true,
  imports: [CommonModule],
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
    <div class="kd" [style.--dr]="renk()">
      <span class="kd-ikon">{{ ikon() }}</span>
      <span class="kd-durum">{{ durum() }}</span>
      @if (d('notlar')) { <span class="kd-not">{{ d('notlar') }}</span> }
    </div>
  `,
  styles: [`
    .kd { display:flex; align-items:center; gap:8px; padding:6px 10px; border-left:3px solid var(--dr,#94a3b8); border-radius:0 8px 8px 0; background:color-mix(in srgb, var(--dr) 6%, transparent); }
    .kd-ikon { font-size:16px; }
    .kd-durum { font-size:13px; font-weight:700; color:var(--dr,#333); text-transform:uppercase; letter-spacing:.5px; }
    .kd-not { font-size:12px; color:var(--py-metin-ikincil,#666); margin-left:auto; }
  `],
})
export class CamKutukDurumKartSadeComponent extends FikoYTKokBilesenComponent {
  parametreler = input<any>();
  constructor() { super(); }
  p = computed(() => this.parametreler()?.ek || {});
  d(alan: string): any { return paramDeger(this.p(), alan); }
  durum = computed(() => this.d('durum') || 'Bekliyor');
  renk = computed(() => DURUM_RENK[this.durum()] || '#94a3b8');
  ikon = computed(() => DURUM_IKON[this.durum()] || '🔄');
}

@Component({
  selector: 'cam-kutuk-durum-edit',
  standalone: true,
  imports: [CommonModule],
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
    <div class="kde">
      <div class="kde-baslik">🔄 Durum Güncelleme</div>
      <div class="kde-butonlar">
        @for (dr of durumlar; track dr) {
          <button class="kde-btn" [class.kde-aktif]="p()?.durum === dr"
            [style.--dr]="DURUM_RENK[dr]" (click)="sec(dr)">
            {{ DURUM_IKON[dr] }} {{ dr }}
          </button>
        }
      </div>
    </div>
  `,
  styles: [`
    .kde { display:flex; flex-direction:column; gap:8px; padding:4px 0; }
    .kde-baslik { font-size:13px; font-weight:600; color:var(--py-metin-ucuncul,#999); }
    .kde-butonlar { display:flex; gap:4px; flex-wrap:wrap; }
    .kde-btn { padding:6px 12px; border:1px solid var(--py-kenarlik,#e5e7eb); border-radius:6px; background:transparent; cursor:pointer; font-size:12px; font-weight:500; color:var(--py-metin-ikincil,#666); transition:all .15s;
      &:hover { border-color:var(--dr); color:var(--dr); }
    }
    .kde-aktif { border-color:var(--dr) !important; color:var(--dr) !important; background:color-mix(in srgb, var(--dr) 12%, transparent) !important; font-weight:700; }
  `],
})
export class CamKutukDurumEditComponent extends FikoYTKokBilesenComponent {
  parametreler = model<any>();
  durumlar = ['Bekliyor', 'Görüldü', 'Hazırlanıyor', 'Tamamlandı', 'İptal'];
  DURUM_RENK = DURUM_RENK;
  DURUM_IKON = DURUM_IKON;
  constructor() { super(); }
  p = computed(() => this.parametreler()?.ek || {});
  sec(durum: string) {
    const params = this.parametreler();
    if (params?.ek) { params.ek.durum = durum; this.parametreler.set({ ...params }); }
  }
}

@DinamikBilesen({
  id: 'cam-kutuk-durum',
  baslik: 'Kütük Durum',
  aciklama: 'Kütük durum güncelleme',
  ikon: 'sync',
  kategori: 'cam-isleme',
})
class CamKutukDurum {
  paylas_kutusu = CamKutukDurumEditComponent;
  paylasilmis_kart_sade = CamKutukDurumKartSadeComponent;
}
